import * as React from 'react';
import { RoughNotation, type RoughNotationProps } from 'react-rough-notation';

export type MarkerTone = 'ink' | 'teal' | 'coral' | 'race';

const toneHex: Record<MarkerTone, string> = {
  ink:   '#332e26',
  teal:  '#3a8a8a',
  coral: '#c84d2a',
  race:  '#f5d000',
};

interface BaseProps {
  /** Color of the marker stroke. Default ink. */
  tone?: MarkerTone;
  /** Defaults to true — render the stroke immediately. Pair with IntersectionObserver
   *  in the consuming component if you need scroll-triggered draw-on. */
  show?: boolean;
  /** Stroke width in pixels (rough-notation `strokeWidth`). Default 2.5. */
  strokeWidth?: number;
  /** Animation duration in ms. Default 800. */
  duration?: number;
  /** Delay before draw-on begins. Default 0. */
  delay?: number;
  /** Iterate roughness for hand-drawn feel. Default 2 (slightly wobbly). */
  iterations?: number;
  children: React.ReactNode;
}

interface MarkerCircleProps extends BaseProps {
  /** Padding around the circled element. Default 6. */
  padding?: number;
}

/**
 * MarkerCircle — animated marker circle around inline content.
 * Uses react-rough-notation for the hand-drawn feel.
 *
 * Example:
 *   <MarkerCircle tone="teal"><b>this</b></MarkerCircle>
 */
export function MarkerCircle({
  tone = 'ink',
  show = true,
  strokeWidth = 2.5,
  duration = 800,
  delay = 0,
  iterations = 2,
  padding = 6,
  children,
}: MarkerCircleProps) {
  return (
    <RoughNotation
      type="circle"
      color={toneHex[tone]}
      strokeWidth={strokeWidth}
      animationDuration={duration}
      animationDelay={delay}
      iterations={iterations}
      padding={padding}
      show={show}
    >
      {children}
    </RoughNotation>
  );
}

interface MarkerHighlightProps extends BaseProps {}

/**
 * MarkerHighlight — yellow-pen highlighter pass under inline text.
 * Defaults to race-yellow tone (the brand highlighter).
 */
export function MarkerHighlight({
  tone = 'race',
  show = true,
  duration = 700,
  delay = 0,
  iterations = 1,
  children,
}: MarkerHighlightProps) {
  return (
    <RoughNotation
      type="highlight"
      color={toneHex[tone]}
      animationDuration={duration}
      animationDelay={delay}
      iterations={iterations}
      show={show}
    >
      {children}
    </RoughNotation>
  );
}

interface MarkerUnderlineProps extends BaseProps {}

/**
 * MarkerUnderline — hand-drawn underline beneath inline text.
 */
export function MarkerUnderline({
  tone = 'teal',
  show = true,
  strokeWidth = 2.5,
  duration = 700,
  delay = 0,
  iterations = 1,
  children,
}: MarkerUnderlineProps) {
  return (
    <RoughNotation
      type="underline"
      color={toneHex[tone]}
      strokeWidth={strokeWidth}
      animationDuration={duration}
      animationDelay={delay}
      iterations={iterations}
      show={show}
    >
      {children}
    </RoughNotation>
  );
}

interface MarkerBracketProps extends BaseProps {
  /** Which sides bracket. Default left + right. */
  brackets?: Array<'left' | 'right' | 'top' | 'bottom'>;
}

/**
 * MarkerBracket — hand-drawn brackets around content (e.g. grouping a passage).
 */
export function MarkerBracket({
  tone = 'ink',
  show = true,
  strokeWidth = 2.5,
  duration = 800,
  delay = 0,
  iterations = 2,
  brackets = ['left', 'right'],
  children,
}: MarkerBracketProps) {
  return (
    <RoughNotation
      type="bracket"
      color={toneHex[tone]}
      strokeWidth={strokeWidth}
      animationDuration={duration}
      animationDelay={delay}
      iterations={iterations}
      brackets={brackets}
      show={show}
    >
      {children}
    </RoughNotation>
  );
}

/** Wrap a region to coordinate multiple marker strokes (animate together / staggered). */
export { RoughNotationGroup } from 'react-rough-notation';
export type { RoughNotationProps };
